<template>
  <div class="credit-page i-px-12 i-py-13">
    <i-spinner
      v-if="showPageLoading"
      data-testid="loading-credit"
      class="page-loading"
      size="xl"
    />
    <div v-else-if="canUseCredit" class="credit-enabled">
      <empty-page
        v-if="error"
        icon="large-link-broken"
        title="Oops, let's try again"
        subtitle="Fetching data can be hard at times, let's give it another go."
        button-text="Refresh page"
        @on-button-click="reloadPage"
      />
      <empty-page
        v-else-if="hasNoDocuments"
        title="Credit Insights has launched!"
        subtitle="Please upload a financial document to check your customers’ credit worthiness."
        icon="large-graph-2"
        button-text="Upload documents"
        @on-button-click="openUpload"
      />
      <empty-page
        v-else-if="showLowScoreEmptyPage"
        title="Confidence Score Too Low for Credit Intelligence "
        subtitle="The Confidence Score for your customer's documents is too low to display accurate financial insights. Please review the documents manually."
        icon="large-graph-stats-descend"
      />
      <div v-else class="i-pb-12">
        <credit-header
          :low-confidence-score="!hasHighConfidenceScore"
          :low-trust-score="
            credit?.summary?.customer_contains_low_trust_documents
          "
        />
        <daily-balance-graph-container />
        <credit-monthly-table-view v-if="creditMonthlyTableViewEnabled" />
        <credit-widgets />
      </div>
    </div>
    <upsell-screen-credit v-else />
  </div>
</template>

<script>
import { iSpinner } from '@inscribe/signal';
import CreditHeader from '@/V2/credit/components/CreditHeader';
import CreditWidgets from '@/V2/credit/CreditWidgets';
import CreditMonthlyTableView from '@/V2/credit/components/CreditMonthlyTableView';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import UpsellScreenCredit from '@/V2/components/upsell/UpsellScreenCredit';
import DailyBalanceGraphContainer from '@/V2/credit/charts/daily-balance-graph/DailyBalanceGraphContainer';
import { getAndSyncQueryParams } from '@/utils/queryParams';
import { getUseAllDocsQueryParameter } from '@/V2/credit/utils';
import { $event } from '@/bus';
import EmptyPage from '@/V2/components/EmptyPage';

export default {
  name: 'Credit',
  components: {
    CreditHeader,
    CreditWidgets,
    EmptyPage,
    iSpinner,
    UpsellScreenCredit,
    DailyBalanceGraphContainer,
    CreditMonthlyTableView,
  },
  beforeRouteLeave(to, from, next) {
    this.clearCreditData();
    next();
  },
  data() {
    return {
      loading: true,
      error: false,
      numberOfDocs: null,
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
    };
  },
  computed: {
    ...mapGetters({
      canUseCredit: 'canUseCredit',
      creditConfidenceScoreThreshold: 'creditConfidenceScoreThreshold',
      creditMonthlyTableViewEnabled: 'creditMonthlyTableViewEnabled',
      hideCreditWhenLowConfidenceScore: 'hideCreditWhenLowConfidenceScore',
    }),
    showLowScoreEmptyPage() {
      return (
        this.hideCreditWhenLowConfidenceScore && !this.hasHighConfidenceScore
      );
    },
    ...mapState({
      credit: 'credit',
    }),
    hasHighConfidenceScore() {
      return (
        this.credit.summary?.confidence_score >=
        this.creditConfidenceScoreThreshold
      );
    },
    showPageLoading() {
      return this.loading || this.canUseCredit === undefined;
    },
    hasNoDocuments() {
      return this.numberOfDocs === 0;
    },
    useAllDocsQueryParameter() {
      return getUseAllDocsQueryParameter(this.params);
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        const oldUseAllDocsValue = this.params?.use_all_docs;
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
        if (oldUseAllDocsValue !== this.params?.use_all_docs) {
          this.updateAllData();
        }
      },
    },
  },
  created() {
    $event.$on('credit:refresh', () => {
      this.updateAllData();
    });
  },
  mounted() {
    this.updateAllData();
  },
  methods: {
    ...mapMutations(['clearCreditData', 'setStartDate', 'setEndDate']),
    ...mapActions([
      'getCreditSummary',
      'getCustomerLoanInsights',
      'getCustomerExpenditureInsights',
      'getCustomerRiskInsights',
      'getCustomerCashflowInsights',
      'getCustomerIncomeInsights',
      'getCustomerBankAccounts',
      'getCreditInsightsByMonth',
    ]),
    async updateAllData() {
      this.clearCreditData();
      this.checkConfidenceScore();
      this.fetchLoanInsights();
      this.fetchExpenditureInsights();
      this.fetchRiskInsights();
      this.fetchCashflowInsights();
      this.fetchIncomeInsights();
      this.fetchBankAccounts();
      if (this.creditMonthlyTableViewEnabled) {
        this.fetchCreditInsightsByMonth();
      }
    },
    async checkConfidenceScore() {
      this.error = false;
      try {
        await this.getCreditSummary({
          customerId: this.$route.params.customer_id,
          queryParams: {
            include_unreconciled_documents: this.useAllDocsQueryParameter,
          },
        });
        if (!this.hasHighConfidenceScore) {
          const docs = await this.fetchDocs();
          this.numberOfDocs = docs.data.data.length;
        }
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async fetchLoanInsights() {
      await this.getCustomerLoanInsights({
        customerId: this.$route.params.customer_id,
        queryParams: {
          include_unreconciled_documents: this.useAllDocsQueryParameter,
        },
      });
    },
    async fetchExpenditureInsights() {
      await this.getCustomerExpenditureInsights({
        customerId: this.$route.params.customer_id,
        queryParams: {
          include_unreconciled_documents: this.useAllDocsQueryParameter,
        },
      });
    },
    async fetchRiskInsights() {
      await this.getCustomerRiskInsights({
        customerId: this.$route.params.customer_id,
        queryParams: {
          include_unreconciled_documents: this.useAllDocsQueryParameter,
        },
      });
    },
    async fetchCashflowInsights() {
      try {
        await this.getCustomerCashflowInsights({
          customerId: this.$route.params.customer_id,
          queryParams: {
            include_unreconciled_documents: this.useAllDocsQueryParameter,
          },
        });
        this.setTransactionRange(this.credit?.customerCashflow?.bank_accounts);
      } catch {
        // TODO: Handle error
      }
    },
    async fetchIncomeInsights() {
      await this.getCustomerIncomeInsights({
        customerId: this.$route.params.customer_id,
        queryParams: {
          include_unreconciled_documents: this.useAllDocsQueryParameter,
        },
      });
    },
    async fetchBankAccounts() {
      await this.getCustomerBankAccounts({
        customerId: this.$route.params.customer_id,
        queryParams: {
          size: 100,
          include_unreconciled_documents: this.useAllDocsQueryParameter,
        },
      });
    },
    async fetchCreditInsightsByMonth() {
      await this.getCreditInsightsByMonth({
        customerId: this.$route.params.customer_id,
        queryParams: {
          include_unreconciled_documents: this.useAllDocsQueryParameter,
        },
      });
    },
    setTransactionRange(bankAccounts) {
      let oldestDate = new Date();
      let latestDate = new Date(0);
      if (bankAccounts) {
        Object.keys(bankAccounts).forEach((key, idx) => {
          const dates = Object.keys(bankAccounts[key].daily_balances);
          const accountStartDate = new Date(dates[0]);
          if (accountStartDate.getTime() < oldestDate.getTime()) {
            oldestDate = accountStartDate;
            this.setStartDate(accountStartDate);
          }
          const accountEndDate = new Date(dates[dates.length - 1]);
          if (accountEndDate.getTime() > latestDate.getTime()) {
            latestDate = accountEndDate;
            this.setEndDate(accountEndDate);
          }
        });
      }
    },
    fetchDocs() {
      return this.$store.dispatch('getPaginatedDocuments', {
        customerId: this.$route.params.customer_id,
        page: 1,
      });
    },
    reloadPage() {
      window.location.reload();
    },
    openUpload() {
      this.$router.push({
        name: 'customer-overview',
        params: {
          customer_id: this.$route.params.customer_id,
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.credit-page {
  height: 100%;
  background: $light-neutral-default;
  overflow: auto;

  @media screen and (max-width: 1312px) {
    max-width: 1048px;
  }

  .credit-enabled {
    height: 100%;
    max-width: 1200px;
    margin: auto;

    .page-loading {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }
}
</style>
